import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Modal, Form, Input, message, Card, Space, Tag, Select, Row, Col,
  Tooltip, Popconfirm, Typography, Empty, Segmented, DatePicker, Switch,
  InputNumber, Table, Pagination, Spin, Divider
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined,
  AppstoreOutlined, BarsOutlined, StarOutlined, StarFilled,
  EnvironmentOutlined, ShoppingOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios默认URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { TextArea } = Input;
const { Text, Title } = Typography;

// 状态元数据
const STATUS_META = {
  in_use: { label: '在用', color: '#52c41a', bg: '#f6ffed', badge: 'mod-badge-success' },
  idle: { label: '闲置', color: '#fa8c16', bg: '#fff7e6', badge: 'mod-badge-warning' },
  pending: { label: '待处理', color: '#f5222d', bg: '#fff1f0', badge: 'mod-badge-danger' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label }));

// 获取鉴权头（保留原有逻辑）
const authHeader = () => ({
  headers: {
    'Authorization': `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
  }
});

const parseTags = (str) => (str ? String(str).split(',').map((s) => s.trim()).filter(Boolean) : []);

const warrantyInfo = (dateStr) => {
  if (!dateStr) return null;
  const d = dayjs(String(dateStr).slice(0, 10));
  if (!d.isValid()) return null;
  const days = d.diff(dayjs().startOf('day'), 'day');
  if (days < 0) return { text: `保修已过 ${-days} 天`, color: '#f5222d', urgent: true };
  if (days === 0) return { text: '保修今天到期', color: '#f5222d', urgent: true };
  if (days <= 30) return { text: `保修 ${days} 天后到期`, color: '#fa8c16', urgent: true };
  return { text: `保修至 ${d.format('YYYY-MM-DD')}`, color: '#8c8c8c', urgent: false };
};

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
};

const Items = () => {
  const { initialize } = useAuthStore();
  const [items, setItems] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ search: '', type: 'all', status: 'all', starred: false, sort: 'recent' });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 24, total: 0 });
  const [viewMode, setViewMode] = useState('card');

  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/item-locations', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: filters.search,
          type: filters.type,
          status: filters.status,
          starred: filters.starred ? 'true' : '',
          sort: filters.sort,
        }
      });
      if (response.data.success) {
        setItems(response.data.locations || []);
        if (response.data.pagination) {
          setPagination(prev => ({ ...prev, total: response.data.pagination.total || 0 }));
        }
      }
    } catch (error) {
      console.error('获取物品位置列表失败:', error);
      message.error('获取物品位置列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchItemTypes = useCallback(async () => {
    try {
      const response = await axios.get('/item-locations/types');
      if (response.data.success) {
        setItemTypes(response.data.types);
      }
    } catch (error) {
      console.error('获取物品类型列表失败:', error);
    }
  }, []);

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get('/item-locations/stats/overview');
      if (response.data.success) setOverview(response.data.overview);
    } catch (error) { /* ignore */ }
  }, []);

  useEffect(() => { initialize(); }, [initialize]);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchItemTypes(); fetchOverview(); }, [fetchItemTypes, fetchOverview]);

  const setFilter = (patch) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setFilters(f => ({ ...f, ...patch }));
  };

  // ---- 增删改 ----
  const openAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ status: 'in_use', quantity: 1, is_starred: false });
    setModalVisible(true);
  };

  const openEdit = (record) => {
    setEditingItem(record);
    // 计算物品类型的预填值：优先按ID匹配，否则按名称匹配（保留原逻辑）
    let presetType = record.item_type != null ? String(record.item_type) : '';
    const candidateNames = [record.type_name, record.item_type]
      .filter(Boolean)
      .map(v => String(v).trim());

    if (itemTypes && itemTypes.length > 0) {
      const hasIdMatch = itemTypes.some(t => String(t.id) === presetType);
      if (!hasIdMatch) {
        const byName = itemTypes.find(t =>
          candidateNames.includes(String(t.name || t.type_name))
        );
        if (byName) {
          presetType = String(byName.id);
        }
      }
    }

    setModalVisible(true);
    setTimeout(() => {
      form.setFieldsValue({
        item_name: record.item_name || '',
        storage_location: record.storage_location || '',
        item_type: presetType || undefined,
        remark: record.remark || '',
        quantity: record.quantity != null ? Number(record.quantity) : 1,
        item_value: record.item_value != null ? Number(record.item_value) : undefined,
        purchase_date: record.purchase_date ? dayjs(String(record.purchase_date).slice(0, 10)) : null,
        warranty_until: record.warranty_until ? dayjs(String(record.warranty_until).slice(0, 10)) : null,
        status: record.status || 'in_use',
        tags: parseTags(record.tags),
        is_starred: !!record.is_starred,
        backup_field1: record.backup_field1 || '',
        backup_field2: record.backup_field2 || '',
        backup_field3: record.backup_field3 || '',
      });
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        item_type: v.item_type || '',
        tags: (v.tags || []).join(','),
        purchase_date: v.purchase_date ? v.purchase_date.format('YYYY-MM-DD') : null,
        warranty_until: v.warranty_until ? v.warranty_until.format('YYYY-MM-DD') : null,
        is_starred: !!v.is_starred,
      };

      if (editingItem) {
        const response = await axios.put(`/item-locations/${editingItem.id}`, payload, authHeader());
        if (response.data.success) {
          message.success('物品位置更新成功');
        }
      } else {
        const response = await axios.post('/item-locations', payload, authHeader());
        if (response.data.success) {
          message.success('物品位置添加成功');
        }
      }

      fetchItems();
      fetchOverview();
      setModalVisible(false);
      setEditingItem(null);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
      const msg = error.response?.data?.message || error.message || '操作失败';
      message.error(msg);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/item-locations/${id}`, authHeader());
      if (response.data.success) {
        message.success('物品位置删除成功');
        fetchItems();
        fetchOverview();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const toggleStar = async (record) => {
    try {
      const res = await axios.put(`/item-locations/${record.id}/star`, {}, authHeader());
      if (res.data.success) {
        setItems(list => list.map(i => (i.id === record.id ? { ...i, is_starred: res.data.is_starred } : i)));
        fetchOverview();
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // ---- 类型管理（保留原逻辑）----
  const handleTypeSubmit = async () => {
    try {
      const values = await typeForm.validateFields();

      if (editingType) {
        const response = await axios.put(`/item-locations/types/${editingType.id}`, values);
        if (response.data.success) {
          message.success('物品类型更新成功');
        }
      } else {
        const response = await axios.post('/item-locations/types', values);
        if (response.data.success) {
          message.success('物品类型添加成功');
        }
      }

      fetchItemTypes();
      setEditingType(null);
      typeForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
      message.error('操作失败');
    }
  };

  const handleTypeEdit = (record) => {
    setEditingType(record);
    typeForm.setFieldsValue({
      type_name: record.name || record.type_name || '',
      description: record.description || ''
    });
  };

  const handleTypeDelete = async (id) => {
    try {
      const response = await axios.delete(`/item-locations/types/${id}`);
      if (response.data.success) {
        message.success('物品类型删除成功');
        fetchItemTypes();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // ---- 统计卡 ----
  const statCards = [
    { key: 'total', label: '物品总数', value: overview?.total ?? pagination.total ?? 0, icon: '📦', color: '#6366F1', onClick: () => setFilter({ type: 'all', status: 'all', starred: false }) },
    { key: 'value', label: '总价值 (元)', value: fmtMoney(overview?.total_value ?? 0), icon: '💰', color: '#13c2c2', onClick: () => setFilter({ sort: 'value' }) },
    { key: 'warranty', label: '30天内保修到期', value: overview?.warranty_expiring ?? 0, icon: '⏰', color: '#fa8c16', danger: (overview?.warranty_expiring ?? 0) > 0, onClick: () => setFilter({ sort: 'warranty' }) },
    { key: 'idle', label: '闲置数量', value: overview?.idle ?? 0, icon: '💤', color: '#faad14', onClick: () => setFilter({ status: 'idle' }) },
    { key: 'starred', label: '重要收藏', value: overview?.starred ?? 0, icon: '⭐', color: '#eb2f96', onClick: () => setFilter({ starred: true }) },
  ];

  const typeOptions = useMemo(
    () => itemTypes.map(t => ({ value: String(t.id), label: t.name || t.type_name })),
    [itemTypes]
  );

  // 展示物品类型名称（item_type 存的可能是 id 或名称）
  const typeLabel = useCallback((record) => {
    if (record.type_name) return record.type_name;
    const byId = itemTypes.find(t => String(t.id) === String(record.item_type));
    if (byId) return byId.name || byId.type_name;
    return record.item_type || '';
  }, [itemTypes]);

  const renderCard = (item) => {
    const st = STATUS_META[item.status || 'in_use'];
    const w = warrantyInfo(item.warranty_until);
    const tags = parseTags(item.tags);
    const tl = typeLabel(item);
    return (
      <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
        <div className="mod-card">
          <div className="mod-card-head">
            <div className="mod-card-icon" style={{ background: `${st.color}1a`, color: st.color }}>
              <ShoppingOutlined />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={item.item_name}>
                <div className="mod-card-title">{item.item_name}</div>
              </Tooltip>
              <div className="mod-card-sub">
                {tl ? <Tag color="green" style={{ marginInlineEnd: 4 }}>{tl}</Tag> : null}
                {item.quantity != null ? <Text type="secondary" style={{ fontSize: 12 }}>x{item.quantity}</Text> : null}
              </div>
            </div>
            <Tooltip title={item.is_starred ? '取消收藏' : '收藏'}>
              <span style={{ cursor: 'pointer', fontSize: 18, padding: 4 }} onClick={() => toggleStar(item)}>
                {item.is_starred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined style={{ color: '#c0c4cc' }} />}
              </span>
            </Tooltip>
          </div>

          <div className="mod-card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959', fontSize: 13, marginBottom: 6 }}>
              <EnvironmentOutlined style={{ color: '#8c8c8c' }} />
              <Text style={{ fontSize: 12, wordBreak: 'break-all' }}>{item.storage_location}</Text>
            </div>

            <div className="mod-badges">
              <span className={`mod-badge ${st.badge}`}>{st.label}</span>
              {item.item_value != null && Number(item.item_value) > 0
                ? <span className="mod-badge mod-badge-primary">💰 ¥{fmtMoney(item.item_value)}</span> : null}
              {w ? <span className="mod-badge" style={{ color: w.color, background: w.urgent ? '#fff1f0' : '#fafafa' }}>⏰ {w.text}</span> : null}
              {item.purchase_date
                ? <span className="mod-badge" style={{ color: '#8c8c8c', background: '#fafafa' }}>🗓 {String(item.purchase_date).slice(0, 10)}</span> : null}
            </div>

            {tags.length ? (
              <div style={{ marginTop: 8 }}>
                {tags.map(t => <Tag key={t} bordered={false} color="geekblue">{t}</Tag>)}
              </div>
            ) : null}

            {item.remark ? (
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }} ellipsis={{ tooltip: item.remark }}>
                {item.remark}
              </Text>
            ) : null}
          </div>

          <div className="mod-card-actions">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(item)}>编辑</Button>
            <Popconfirm title="确定要删除这个物品吗？" onConfirm={() => handleDelete(item.id)} okText="确定" cancelText="取消">
              <Button size="small" type="text" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </div>
        </div>
      </Col>
    );
  };

  const tableColumns = [
    {
      title: '物品', dataIndex: 'item_name', key: 'item_name',
      render: (name, record) => (
        <Space>
          <ShoppingOutlined style={{ color: STATUS_META[record.status || 'in_use'].color }} />
          <span>
            {record.is_starred ? <StarFilled style={{ color: '#faad14', marginInlineEnd: 4 }} /> : null}
            <Text strong>{name}</Text>
            {record.quantity != null ? <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 6 }}>x{record.quantity}</Text> : null}
          </span>
        </Space>
      ),
    },
    { title: '类型', key: 'item_type', width: 100, render: (_, r) => { const t = typeLabel(r); return t ? <Tag color="green">{t}</Tag> : '-'; } },
    {
      title: '存放位置', dataIndex: 'storage_location', key: 'storage_location',
      render: (loc) => <Text code style={{ fontSize: 12 }}>{loc}</Text>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (s) => { const m = STATUS_META[s || 'in_use']; return <Tag style={{ color: m.color, background: m.bg, border: 'none' }}>{m.label}</Tag>; },
    },
    {
      title: '价值', dataIndex: 'item_value', key: 'item_value', width: 100,
      render: (v) => (v != null && Number(v) > 0 ? <Text>¥{fmtMoney(v)}</Text> : '-'),
    },
    {
      title: '保修到期', dataIndex: 'warranty_until', key: 'warranty_until', width: 130,
      render: (d) => { const w = warrantyInfo(d); return w ? <Text style={{ color: w.color, fontSize: 12 }}>{w.text}</Text> : '-'; },
    },
    {
      title: '操作', key: 'action', width: 130, fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button size="small" type="text" icon={record.is_starred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} onClick={() => toggleStar(record)} />
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)} okText="删除" cancelText="取消">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="mod-page">
      {/* 顶栏 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">物品管理</div>
          <div className="mod-header-subtitle">登记物品的存放位置、数量与价值，追踪保修与闲置状态</div>
        </div>
        <Space>
          <Button icon={<SettingOutlined />} onClick={() => setTypeModalVisible(true)}>类型管理</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加物品</Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        {statCards.map((s) => (
          <div
            key={s.key}
            className={`mod-stat-card ${s.onClick ? 'clickable' : ''} ${s.danger ? 'danger' : ''}`}
            onClick={s.onClick}
          >
            <div className="mod-stat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div>
              <div className="mod-stat-value" style={{ color: s.danger ? '#f5222d' : undefined }}>{s.value}</div>
              <div className="mod-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 工具栏 */}
      <Card className="mod-toolbar" bodyStyle={{ padding: 12 }}>
        <div className="mod-toolbar-row">
          <Input.Search
            allowClear
            placeholder="搜索物品名称 / 位置 / 标签 / 备注"
            defaultValue={filters.search}
            onSearch={(v) => setFilter({ search: v })}
            style={{ maxWidth: 300, flex: '1 1 220px' }}
          />
          <Select value={filters.type} onChange={(v) => setFilter({ type: v })} style={{ width: 140 }}
            options={[{ value: 'all', label: '全部类型' }, ...typeOptions]} />
          <Select value={filters.status} onChange={(v) => setFilter({ status: v })} style={{ width: 130 }}
            options={[{ value: 'all', label: '全部状态' }, ...STATUS_OPTIONS]} />
          <Select value={filters.sort} onChange={(v) => setFilter({ sort: v })} style={{ width: 140 }}
            options={[
              { value: 'recent', label: '最近添加' },
              { value: 'name', label: '按名称' },
              { value: 'value', label: '按价值' },
              { value: 'warranty', label: '按保修到期' },
              { value: 'starred', label: '收藏优先' },
            ]} />
          <Button type={filters.starred ? 'primary' : 'default'} icon={filters.starred ? <StarFilled /> : <StarOutlined />}
            onClick={() => setFilter({ starred: !filters.starred })}>收藏</Button>
          <div style={{ marginInlineStart: 'auto' }}>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[{ value: 'card', icon: <AppstoreOutlined /> }, { value: 'table', icon: <BarsOutlined /> }]}
            />
          </div>
        </div>
      </Card>

      {/* 内容 */}
      <Spin spinning={loading}>
        {items.length === 0 ? (
          <Empty description="还没有物品记录，点击「添加物品」登记第一件" style={{ padding: '60px 0' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加物品</Button>
          </Empty>
        ) : viewMode === 'card' ? (
          <Row gutter={[14, 14]} style={{ marginTop: 4 }}>
            {items.map(renderCard)}
          </Row>
        ) : (
          <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden' }}>
            <Table
              rowKey="id"
              columns={tableColumns}
              dataSource={items}
              pagination={false}
              scroll={{ x: 900 }}
              size="middle"
            />
          </Card>
        )}

        {pagination.total > pagination.pageSize && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={false}
              onChange={(page) => setPagination(p => ({ ...p, current: page }))}
              showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
            />
          </div>
        )}
      </Spin>

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingItem ? '编辑物品' : '添加物品'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => { setModalVisible(false); setEditingItem(null); form.resetFields(); }}
        okText="保存"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item name="item_name" label="物品名称" rules={[{ required: true, message: '请输入物品名称' }]}>
                <Input placeholder="如：戴尔笔记本电脑" allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="item_type" label="物品类型">
                <Select allowClear placeholder="选择类型" options={typeOptions} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="storage_location" label="所在位置 / 容器" rules={[{ required: true, message: '请输入存放位置' }]}>
            <TextArea rows={2} placeholder="如：书房抽屉 / 客厅储物柜 / 车库B区" allowClear />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="quantity" label="数量">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="item_value" label="价值金额（元）">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="如：5999" prefix="¥" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="purchase_date" label="购买日期（选填）">
                <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="warranty_until" label="保修到期（选填）" tooltip="临近到期会在统计卡与列表中提示">
                <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="tags" label="标签">
            <Select mode="tags" placeholder="回车添加标签，如 电子 / 贵重 / 待出" tokenSeparators={[',']} />
          </Form.Item>

          <Form.Item name="remark" label="备注说明">
            <TextArea rows={2} placeholder="补充说明" allowClear />
          </Form.Item>

          <Form.Item name="is_starred" label="标记为重要" valuePropName="checked">
            <Switch checkedChildren="⭐" unCheckedChildren="" />
          </Form.Item>

          <Divider style={{ margin: '4px 0 12px' }}>备用字段</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="backup_field1" label="备用字段1">
                <Input placeholder="备用字段1" allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="backup_field2" label="备用字段2">
                <Input placeholder="备用字段2" allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="backup_field3" label="备用字段3">
                <Input placeholder="备用字段3" allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 类型管理弹窗 */}
      <Modal
        title="物品类型管理"
        open={typeModalVisible}
        onCancel={() => { setTypeModalVisible(false); setEditingType(null); typeForm.resetFields(); }}
        footer={null}
        width={480}
      >
        <Form form={typeForm} layout="inline" onFinish={handleTypeSubmit} style={{ marginBottom: 16 }}>
          <Form.Item name="type_name" rules={[{ required: true, message: '请输入类型名' }]}>
            <Input placeholder="类型名称，如 电子/家具/工具" />
          </Form.Item>
          <Form.Item name="description">
            <Input placeholder="描述（选填）" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">{editingType ? '更新' : '添加'}</Button>
            {editingType && <Button style={{ marginInlineStart: 8 }} onClick={() => { setEditingType(null); typeForm.resetFields(); }}>取消</Button>}
          </Form.Item>
        </Form>
        <div>
          {itemTypes.length === 0 ? <Empty description="暂无类型" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <Space wrap>
              {itemTypes.map((t) => (
                <Tag key={t.id} closable
                  onClose={(e) => { e.preventDefault(); handleTypeDelete(t.id); }}
                  onClick={() => handleTypeEdit(t)}
                  style={{ cursor: 'pointer', padding: '4px 10px', fontSize: 13 }} color="green">
                  {t.name || t.type_name}
                </Tag>
              ))}
            </Space>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Items;
